import { Link } from "react-router-dom";
import Loading from "../components/Loading";
import ProjectCard from "../components/ProjectCard";
import { formatCompactNumber } from "../helpers/helpers";

const Landing = ({ profile, repos, loading, error, stats }) => {
  const rfc = profile.rfcResearch || {};

  return (
    <div className="page home-page" id="home">
      <header className="site-header">
        <div className="content-width site-header-inner">
          <div className="brand">
            <div className="brand-icon">
              <span className="material-symbols-outlined">terminal</span>
            </div>
            <span>{profile.brand}</span>
          </div>

          <nav className="site-nav" aria-label="Primary">
            <a href="#home">Home</a>
            <a href="#projects">Projects</a>
            <a href="#publication">Publication</a>
            <a href="#stack">Stack</a>
            <a href="#contact">Contact</a>
            <Link to="/bio" className="resume-chip">
              Resume
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero-section content-width">
          <div className="hero-copy">
            <p className="status-pill">
              <span className="material-symbols-outlined">verified</span>
              {profile.availability}
            </p>

            <h1>{profile.heroHeadline}</h1>
            <p>{profile.heroSummary}</p>

            <div className="hero-actions">
              <a className="btn btn-primary" href={rfc.arxivUrl} target="_blank" rel="noreferrer">
                Read arXiv Paper
                <span className="material-symbols-outlined">open_in_new</span>
              </a>
              <Link className="btn btn-secondary" to="/bio">
                Experience & Resume
              </Link>
              <a className="btn btn-text" href={profile.githubUrl} target="_blank" rel="noreferrer">
                GitHub
              </a>
            </div>
          </div>

          <aside className="hero-publication glass-card" aria-label="Featured publication">
            <div className="publication-visual" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </div>
            <p className="eyebrow">{rfc.arxivId}</p>
            <h2>{rfc.title}</h2>
            <p>{rfc.status}</p>
            <dl className="paper-meta">
              <div>
                <dt>Field</dt>
                <dd>{rfc.subjects?.join(" / ")}</dd>
              </div>
              <div>
                <dt>Record</dt>
                <dd>{rfc.comments}</dd>
              </div>
            </dl>
          </aside>
        </section>

        <section className="projects-section content-width" id="projects">
          <div className="section-head">
            <div>
              <h2>Open Source Impact</h2>
              <p>Live repositories pulled from GitHub.</p>
            </div>

            <div className="stats-panel glass-card">
              <div>
                <p>{formatCompactNumber(stats.stars)}</p>
                <span>Stars</span>
              </div>
              <div>
                <p>{formatCompactNumber(stats.forks)}</p>
                <span>Forks</span>
              </div>
              <div>
                <p>{formatCompactNumber(stats.projects)}</p>
                <span>Projects</span>
              </div>
            </div>
          </div>

          {loading ? <Loading /> : null}
          {!loading && error ? <p className="error-message">{error}</p> : null}

          {!loading && !error ? (
            repos.length > 0 ? (
              <div className="project-grid">
                {repos.map((repo) => (
                  <ProjectCard key={repo.id} repo={repo} />
                ))}
              </div>
            ) : (
              <p className="empty-message">No repositories available right now.</p>
            )
          ) : null}
        </section>

        <section className="stack-section content-width" id="stack">
          <h2>Tech Stack & Tools</h2>
          <p>Core technologies used in current projects.</p>
          <div className="chip-grid">
            {profile.stackTools.map((tool) => (
              <span key={tool} className="tool-chip glass-card">
                {tool}
              </span>
            ))}
          </div>
        </section>

        <section className="rfc-section content-width" id="publication">
          <div className="rfc-card glass-card">
            <p className="eyebrow">Published Research</p>
            <h2>Resonant Field Computing</h2>
            <p className="rfc-status">{rfc.status}</p>
            <h3>{rfc.title}</h3>
            <p>{rfc.summary}</p>
            {rfc.highlights?.length > 0 ? (
              <ul>
                {rfc.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            ) : null}
            <div className="rfc-link-row">
              <a className="btn btn-primary" href={rfc.arxivUrl} target="_blank" rel="noreferrer">
                arXiv Record
                <span className="material-symbols-outlined">open_in_new</span>
              </a>
              <a className="btn btn-secondary" href={rfc.pdfUrl} target="_blank" rel="noreferrer">
                PDF
              </a>
              <a className="btn btn-text" href={rfc.doiUrl} target="_blank" rel="noreferrer">
                DOI
              </a>
            </div>
            <p className="rfc-source">
              Source: {rfc.arxivId} / {rfc.source || "local manuscript"}
            </p>
          </div>
        </section>

        <section className="contact-section content-width" id="contact">
          <div className="contact-card glass-card">
            <h2>{profile.name}</h2>
            <p>{profile.bio}</p>
            <div className="contact-actions">
              {profile.contactLinks.map((link) => (
                <a key={link.label} href={link.href} target="_blank" rel="noreferrer" className="btn btn-secondary">
                  <span className="material-symbols-outlined">{link.icon}</span>
                  {link.label}
                </a>
              ))}
              <Link className="btn btn-primary" to="/bio">
                View Full Experience
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="content-width">
          <p>(c) {new Date().getFullYear()} {profile.name}. Built with React and live GitHub data.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
